import { supabase } from '@/integrations/supabase/client';

/**
 * THE canonical atomic ticket-creation primitive (client side).
 *
 * Wraps the `create_ticket_bundle` RPC (migration 20260807130000). Every row a
 * valid ticket REQUIRES — the ticket, its `jobs`, and their `ticket_jobs` links —
 * commits together or not at all, for every ticket in the bundle.
 *
 * Before this existed, Create Ticket issued those as independent client
 * statements. On 2026-08-07 the third failed a CHECK constraint and the first two
 * stayed committed, leaving a live Active ticket with an empty Jobs section on the
 * Dayboard from a workflow that had just reported "Error Creating Ticket".
 * Nothing can roll back an already-committed client statement, so the boundary
 * has to be server-side.
 *
 * ── WHY A BUNDLE ────────────────────────────────────────────────────────────
 * Two callers need this and both are logically all-or-nothing:
 *
 *   createTicketWithAssignment  one ticket + its jobs (+ rollover continuations)
 *   handleConfirmSplit          two or more tickets, EACH with its own job set
 *
 * Calling a single-ticket RPC twice would not make the split atomic — Part A
 * would commit and stay committed when Part B failed, the same orphan class one
 * level up. So the primitive is a bundle: the normal flow passes ONE group, the
 * split flow passes two. One primitive, no second RPC to keep in sync.
 *
 * Groups commit in array order, so a later group may reference an earlier group's
 * ticket id (`parent_ticket_id` / `rollover_of_ticket_id`).
 *
 * Deliberately NOT included: `parts_lines`, `assignment_audit`, `rollover_audit`,
 * advisor/analytics writes. Those are optional side effects — their failure must
 * not destroy an otherwise valid ticket — so they stay outside the transaction.
 *
 * The RPC is SECURITY INVOKER, so RLS applies to the caller exactly as it did to
 * the direct inserts this replaces. Verified end to end as a real `authenticated`
 * user, including that a cross-shop payload is refused with zero residue.
 */
export interface TicketGroupInput {
  /** The `tickets` row. Only supplied keys are written; the rest keep their DEFAULTs. */
  ticket: Record<string, unknown>;
  /**
   * `jobs` rows for THIS ticket. Order defines the pairing with `ticketJobs`.
   *
   * May be empty ONLY for a rollover continuation (a ticket carrying
   * `rollover_of_ticket_id`), whose work lives on its parent. Any other
   * zero-job group is rejected server-side, because a ticket with no jobs is
   * precisely the invalid object this primitive exists to prevent.
   */
  jobs: Record<string, unknown>[];
  /**
   * `ticket_jobs` rows, index-aligned with `jobs`.
   *
   * Must NOT carry `ticket_id` or `job_id` — the RPC injects both from THIS
   * group's inserted rows, which is what makes it impossible for a link to
   * reference an id that failed to commit, or another group's ticket.
   */
  ticketJobs: Record<string, unknown>[];
  /**
   * B-W5 (LEDGER D-3): what the person's screen showed for a multi-day booking, as the CLIENT attests it. Optional;
   * put on a rollover continuation group. The server records it on the booking-approval record labelled
   * `client_attested` / `verified_by_server: false` — it is never treated as proof that a preview was shown.
   */
  rolloverPreview?: Record<string, unknown>;
}

export interface TicketGroupResult {
  ticketId: string;
  /** Ids of the created `jobs` rows, in the same order as the group's `jobs`. */
  jobIds: string[];
}

export async function createTicketBundle(
  groups: TicketGroupInput[],
): Promise<TicketGroupResult[]> {
  if (groups.length === 0) {
    throw new Error('createTicketBundle: refusing an empty bundle');
  }

  groups.forEach((g, i) => {
    if (g.jobs.length !== g.ticketJobs.length) {
      // Caught client-side too, so the mismatch names the real culprit rather
      // than surfacing as a generic RPC error.
      throw new Error(
        `createTicketBundle: group ${i} has ${g.jobs.length} job(s) but ` +
          `${g.ticketJobs.length} ticketJob(s) — must be index-aligned`,
      );
    }
  });

  const payload = groups.map(g => ({
    ticket: g.ticket,
    jobs: g.jobs,
    ticket_jobs: g.ticketJobs,
    ...(g.rolloverPreview ? { rollover_preview: g.rolloverPreview } : {}),
  }));

  // `src/integrations/supabase/types.ts` is GENERATED from the deployed schema,
  // so it does not yet name this RPC — migration 20260807130000 introduces it.
  // The cast is narrow and temporary: regenerate types after the migration is
  // applied and it can go.
  const { data, error } = await (supabase.rpc as unknown as (
    fn: string,
    args: Record<string, unknown>,
  ) => Promise<{ data: unknown; error: { message: string } | null }>)(
    'create_ticket_bundle',
    { p_groups: payload },
  );

  if (error) {
    // Surface the PostgREST error shape intact — code/details/hint are what
    // identified the original 23514.
    console.error('[createTicketBundle] atomic creation failed', {
      code: (error as { code?: string }).code ?? null,
      message: error.message ?? null,
      details: (error as { details?: string }).details ?? null,
      hint: (error as { hint?: string }).hint ?? null,
      groupCount: groups.length,
      jobCounts: groups.map(g => g.jobs.length),
    });
    throw error;
  }

  const returned = (data as { groups?: unknown } | null)?.groups;
  if (!Array.isArray(returned) || returned.length !== groups.length) {
    throw new Error(
      `createTicketBundle: expected ${groups.length} group result(s), got ${
        Array.isArray(returned) ? returned.length : JSON.stringify(returned)
      }`,
    );
  }

  return returned.map((r, i) => {
    const row = r as { ticket_id?: unknown; job_ids?: unknown };
    if (typeof row.ticket_id !== 'string' || !row.ticket_id) {
      throw new Error(`createTicketBundle: group ${i} returned no ticket id`);
    }
    if (!Array.isArray(row.job_ids) || row.job_ids.length !== groups[i].jobs.length) {
      // An id-count mismatch would silently mis-map staged parts onto the wrong
      // job, so fail rather than proceed on a broken alignment.
      throw new Error(
        `createTicketBundle: group ${i} expected ${groups[i].jobs.length} job id(s), got ${
          Array.isArray(row.job_ids) ? row.job_ids.length : JSON.stringify(row.job_ids)
        }`,
      );
    }
    return { ticketId: row.ticket_id, jobIds: row.job_ids as string[] };
  });
}
